<template>
    <div>
        <content-reset
            v-if="cardText !== ''"
            :body="cardText"
        />

        <div
            v-if="error"
            class="text-red-600 mt-2 text-sm font-medium"
        >
            {{ error || '&nbsp;' }}
        </div>

        <template v-if="isStripe">
            <stripe-form
                ref="stripeFormRef"
                :payment-source-id="paymentSourceData.id"
                :amount="creditCardAmount"
                :is-processing="isProcessing"
                class="mt-2"
                @payment-method="handlePaymentMethod"
            />
        </template>

        <template
            v-else-if="
                paymentSourceData.data?.__typename ===
                'PaymentSourceCreditCardWindcave'
            "
        >
            <windcave-form
                ref="windcaveFormRef"
                :payment-source-id="paymentSourceData.id"
                :payment-source="paymentSourceData.data"
                :is-processing="isProcessing"
                :amount="creditCardAmount"
                @payment-method="handlePaymentMethod"
            />
        </template>

        <template
            v-else-if="
                paymentSourceData.data?.__typename ===
                'PaymentSourceCreditCardCardpointe'
            "
        >
            <hosted-payment-form
                :class="error ? '' : '-mt-6'"
                :payment-source="paymentSourceData.data"
                :is-processing="isProcessing"
                @payment-method="handlePaymentMethod"
            />
        </template>

        <app-message
            v-else
            theme="danger"
        >
            Unable to load payment form. Please refresh or contact the facility
            for more information.
        </app-message>
    </div>
</template>

<script lang="ts">
import { graphql } from '~/resources/graphql'

export const NewPaymentMethodPaymentSourceFragment = graphql(/* GraphQL */ `
    fragment NewPaymentMethodPaymentSourceFragment on PaymentSource {
        id
        cardText(language: ENGLISH)
        data {
            __typename
            ... on PaymentSourceCreditCardCardpointe {
                ...HostedPaymentFormPaymentSourceFragment
            }
        }
    }
`)
</script>

<script setup lang="ts">
import { computed, ref } from 'vue'
import HostedPaymentForm from '~/components/Payments/HostedPaymentForm.vue'
import StripeForm from '~/components/Payments/StripeForm.vue'
import type { Decimal } from '~/composables/support/decimal'
import type {
    PaymentMethodProperties,
    TokenPaymentMethod
} from '~/components/Checkout/Sections/Payment/payment-method'
import { type FragmentType, getFragmentData } from '~/resources/graphql'
import { useCheckoutFormProcessing } from '~/components/Checkout/Forms/use-checkout-form'
import ContentReset from '~/components/ContentReset.vue'
import WindcaveForm from '~/components/Payments/WindcaveForm.vue'
import AppMessage from '~/components/AppMessage.vue'

const props = defineProps<{
    amount: Decimal
    paymentSource: FragmentType<typeof NewPaymentMethodPaymentSourceFragment>
    error?: string
}>()

const emit = defineEmits<{
    (e: 'paymentMethod', payload: PaymentMethodProperties | null): void
}>()

const { isProcessing } = useCheckoutFormProcessing()

const paymentSourceData = computed(() =>
    getFragmentData(NewPaymentMethodPaymentSourceFragment, props.paymentSource)
)

const isStripe = computed(() => {
    return (
        paymentSourceData.value?.data?.__typename ===
        'PaymentSourceCreditCardStripe'
    )
})

const isWindcave = computed(() => {
    return (
        paymentSourceData.value?.data?.__typename ===
        'PaymentSourceCreditCardWindcave'
    )
})

const cardText = computed(() => {
    if (paymentSourceData.value.data === null) {
        return ''
    }

    if (
        paymentSourceData.value.data.__typename !==
        'PaymentSourceCreditCardCardpointe'
    ) {
        return ''
    }

    return paymentSourceData.value.cardText
})

const creditCardAmount = computed(() => props.amount.toFixed(2))

function handlePaymentMethod(
    paymentMethod: TokenPaymentMethod | null | undefined
) {
    if (paymentMethod === null || paymentMethod === undefined) {
        emit('paymentMethod', null)
        return
    }

    emit('paymentMethod', {
        key: paymentMethod.token,
        paymentSourceId: paymentSourceData.value.id,
        displayInfo: {
            isCard: paymentMethod.isCard,
            brand: paymentMethod.brand,
            lastFour: paymentMethod.lastFour,
            accountExpirationDate: paymentMethod.accountExpirationDate,
            lastUsedDate: null
        },
        paymentTodayAllowed: true,
        data: isStripe.value
            ? {
                  type: 'Stripe',
                  token: paymentMethod.token,
                  amount: creditCardAmount.value
              }
            : isWindcave.value
              ? {
                    type: 'WindcaveCreditCard',
                    token: paymentMethod.token
                }
              : {
                    type: 'CardpointeCreditCard',
                    token: paymentMethod.token
                }
    })
}

const stripeFormRef = ref<InstanceType<typeof StripeForm>>()
const windcaveFormRef = ref<InstanceType<typeof WindcaveForm>>()
async function submitPaymentForm() {
    if (stripeFormRef.value !== undefined && stripeFormRef.value !== null) {
        await stripeFormRef.value.submitStripeForm()
    } else if (windcaveFormRef.value !== undefined) {
        await windcaveFormRef.value.submitPaymentMethod()
    }
}

defineExpose({
    submitPaymentForm
})
</script>
